<script setup>
import { ref, computed, watch } from 'vue'
import Drawer from 'primevue/drawer'
import { useI18n } from '@/i18n'
import { useUserStore } from '@/stores/user'
import { useDashboardStore } from '@/stores/dashboard'
import { CATALOG_GROUPS, sourceMeta, vizMeta, wl, defaultConfig } from '@/config/widgetSources'

const props = defineProps({
  visible:   { type: Boolean, default: false },
  sectionId: { type: String,  default: null },
})

const emit = defineEmits(['update:visible'])

const { t, activeLang } = useI18n()
const userStore = useUserStore()
const dashStore = useDashboardStore()

const show = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v),
})

// ── Kategorien-Sortierung (localStorage) ─────────────────────────────────────
const ORDER_KEY = 'secsu-dash-catalog-order'

function loadOrder() {
  try { return JSON.parse(localStorage.getItem(ORDER_KEY)) ?? [] } catch { return [] }
}

const userOrder = ref(loadOrder())
const sorting   = ref(false)

const orderedGroups = computed(() => {
  const visible = CATALOG_GROUPS.filter(g => !g.partnerOnly || userStore.isPartner)
  const rank = new Map(userOrder.value.map((k, i) => [k, i]))
  // Gespeicherte Reihenfolge zuerst, unbekannte/neue Gruppen in Default-Prio dahinter
  return [...visible].sort((a, b) => {
    const ra = rank.has(a.key) ? rank.get(a.key) : 1000 + visible.indexOf(a)
    const rb = rank.has(b.key) ? rank.get(b.key) : 1000 + visible.indexOf(b)
    return ra - rb
  })
})

function moveGroup(key, dir) {
  const keys = orderedGroups.value.map(g => g.key)
  const idx  = keys.indexOf(key)
  const to   = idx + dir
  if (idx < 0 || to < 0 || to >= keys.length) return
  ;[keys[idx], keys[to]] = [keys[to], keys[idx]]
  userOrder.value = keys
  localStorage.setItem(ORDER_KEY, JSON.stringify(keys))
}

// ── Navigation (max. 2 Ebenen) ────────────────────────────────────────────────
const activeGroup = ref(null)   // null = Hauptebene

watch(show, (v) => {
  if (v) { activeGroup.value = null; sorting.value = false }
})

// Vorlagen der aktiven Gruppe auflösen (String = Source-Key, Objekt = Variante)
const activeTemplates = computed(() => {
  if (!activeGroup.value) return []
  return activeGroup.value.templates
    .map((entry) => {
      const key = typeof entry === 'string' ? entry : entry.source
      const src = sourceMeta(key)
      if (!src || (src.partnerOnly && !userStore.isPartner)) return null
      const viz    = typeof entry === 'string' ? src.defaultViz : (entry.viz ?? src.defaultViz)
      const vMeta  = vizMeta(viz)
      return {
        id:     typeof entry === 'string' ? key : `${key}:${viz}:${JSON.stringify(entry.config ?? {})}`,
        source: key,
        viz,
        config: typeof entry === 'string' ? null : (entry.config ?? null),
        label:  wl(typeof entry === 'string' ? src.label : (entry.label ?? src.label), activeLang.value),
        icon:   vMeta?.icon ?? 'pi pi-th-large',
        desc:   wl(vMeta?.label, activeLang.value),
      }
    })
    .filter(Boolean)
})

// ── Hinzufügen (Drawer bleibt offen, kurzes Feedback am Eintrag) ─────────────
const addedId = ref(null)
let addedTimer = null

function addTemplate(tpl) {
  const config = { ...defaultConfig(tpl.viz, tpl.source), ...(tpl.config ?? {}) }
  dashStore.addWidget(props.sectionId, tpl.viz, tpl.source, config)
  addedId.value = tpl.id
  clearTimeout(addedTimer)
  addedTimer = setTimeout(() => { addedId.value = null }, 1200)
}

function addCustomWidget() {
  dashStore.addEmptyWidget(props.sectionId)
  show.value = false
}
</script>

<template>
  <Drawer
    v-model:visible="show"
    position="right"
    class="wcd-drawer"
    :header="t('dashboard.addWidget')"
  >
    <div class="wcd-body">

      <!-- Eigenes Widget — immer oben -->
      <div class="wcd-item wcd-item--custom" @click="addCustomWidget">
        <i class="pi pi-sliders-h wcd-item-icon" />
        <div class="wcd-item-text">
          <div class="wcd-item-name">{{ t('dashboard.customWidget') }}</div>
          <div class="wcd-item-desc">{{ t('dashboard.customWidgetDesc') }}</div>
        </div>
        <i class="pi pi-angle-right wcd-chevron" />
      </div>

      <!-- ── Ebene 1: Hauptgruppen ─────────────────────────────────── -->
      <template v-if="!activeGroup">
        <div class="wcd-groups-head">
          <span class="wcd-groups-title">{{ t('dashboard.categories') }}</span>
          <button
            class="wcd-sort-btn"
            :class="{ active: sorting }"
            :title="t('dashboard.sortCategories')"
            @click="sorting = !sorting"
          >
            <i class="pi pi-sort-alt" />
          </button>
        </div>

        <div
          v-for="(group, idx) in orderedGroups"
          :key="group.key"
          class="wcd-item"
          :class="{ 'wcd-item--static': sorting }"
          @click="!sorting && (activeGroup = group)"
        >
          <i :class="[group.icon, 'wcd-item-icon']" />
          <div class="wcd-item-text">
            <div class="wcd-item-name">{{ wl(group.label, activeLang) }}</div>
            <div class="wcd-item-desc">{{ group.templates.length }} Widgets</div>
          </div>

          <template v-if="sorting">
            <button
              class="wcd-order-btn"
              :disabled="idx === 0"
              @click.stop="moveGroup(group.key, -1)"
            >
              <i class="pi pi-arrow-up" />
            </button>
            <button
              class="wcd-order-btn"
              :disabled="idx === orderedGroups.length - 1"
              @click.stop="moveGroup(group.key, 1)"
            >
              <i class="pi pi-arrow-down" />
            </button>
          </template>
          <i v-else class="pi pi-angle-right wcd-chevron" />
        </div>
      </template>

      <!-- ── Ebene 2: Vorlagen der Gruppe ──────────────────────────── -->
      <template v-else>
        <button class="wcd-back" @click="activeGroup = null">
          <i class="pi pi-arrow-left" />
          <span>{{ t('dashboard.back') }}</span>
        </button>

        <div class="wcd-group-title">
          <i :class="[activeGroup.icon]" />
          <span>{{ wl(activeGroup.label, activeLang) }}</span>
        </div>

        <div
          v-for="tpl in activeTemplates"
          :key="tpl.id"
          class="wcd-item"
          @click="addTemplate(tpl)"
        >
          <i :class="[tpl.icon, 'wcd-item-icon']" />
          <div class="wcd-item-text">
            <div class="wcd-item-name">{{ tpl.label }}</div>
            <div class="wcd-item-desc">{{ tpl.desc }}</div>
          </div>
          <i
            :class="addedId === tpl.id ? 'pi pi-check wcd-added' : 'pi pi-plus wcd-chevron'"
          />
        </div>
      </template>

    </div>
  </Drawer>
</template>

<style scoped>
.wcd-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* ── Gruppen-Kopf ── */
.wcd-groups-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px 4px;
}
.wcd-groups-title {
  font-size: .65rem;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--p-text-color-secondary);
}
.wcd-sort-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-size: .75rem;
  color: var(--p-text-color-secondary);
  transition: background .12s, color .12s;
}
.wcd-sort-btn:hover { background: var(--p-surface-200, rgba(0,0,0,.08)); }
.wcd-sort-btn.active {
  background: var(--p-primary-100, #dbeafe);
  color: var(--p-primary-color, #3b82f6);
}

/* ── Listen-Einträge ── */
.wcd-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--p-content-border-color, rgba(0,0,0,.08));
  cursor: pointer;
  transition: background .12s, border-color .12s;
  user-select: none;
}
.wcd-item:hover {
  background: var(--p-primary-50, #eff6ff);
  border-color: var(--p-primary-200, #bfdbfe);
}
.wcd-item--static { cursor: default; }
.wcd-item--static:hover {
  background: none;
  border-color: var(--p-content-border-color, rgba(0,0,0,.08));
}

.wcd-item--custom {
  border-style: dashed;
  border-color: var(--p-primary-color, #3b82f6);
  margin-bottom: 4px;
}

.wcd-item-icon {
  font-size: 1.05rem;
  color: var(--p-primary-color, #3b82f6);
  flex-shrink: 0;
  width: 22px;
  text-align: center;
}

.wcd-item-text {
  flex: 1;
  min-width: 0;
}
.wcd-item-name {
  font-size: .88rem;
  font-weight: 600;
  color: var(--p-text-color);
}
.wcd-item-desc {
  font-size: .75rem;
  color: var(--p-text-color-secondary);
  margin-top: 1px;
}

.wcd-chevron {
  font-size: .75rem;
  color: var(--p-text-color-secondary);
  opacity: .6;
  flex-shrink: 0;
}
.wcd-added {
  font-size: .8rem;
  color: var(--p-green-500, #22c55e);
  flex-shrink: 0;
}

/* ── Sortier-Buttons ── */
.wcd-order-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: var(--p-surface-100, #f1f5f9);
  cursor: pointer;
  font-size: .65rem;
  color: var(--p-text-color-secondary);
  flex-shrink: 0;
}
.wcd-order-btn:hover:not(:disabled) {
  background: var(--p-surface-200, rgba(0,0,0,.1));
  color: var(--p-text-color);
}
.wcd-order-btn:disabled { opacity: .3; cursor: default; }

/* ── Ebene 2 ── */
.wcd-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: .82rem;
  font-weight: 600;
  color: var(--p-primary-color, #3b82f6);
}
.wcd-back:hover { text-decoration: underline; }

.wcd-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 4px 8px;
  font-size: .8rem;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--p-text-color-secondary);
}

/* ── Dark mode ── */
:global(.my-app-dark) .wcd-item:hover {
  background: var(--p-surface-800, #1e293b);
  border-color: var(--p-primary-700, #1d4ed8);
}
:global(.my-app-dark) .wcd-item--static:hover {
  background: none;
  border-color: var(--p-content-border-color, rgba(255,255,255,.1));
}
:global(.my-app-dark) .wcd-sort-btn.active {
  background: rgba(59,130,246,.18);
}
:global(.my-app-dark) .wcd-order-btn {
  background: var(--p-surface-700, #334155);
}
</style>

<style>
/* Drawer-Breite (unscoped — PrimeVue rendert das Panel im Body) */
.wcd-drawer {
  width: 420px !important;
  max-width: 92vw !important;
}
</style>
